import { NormalizedHttpFailure } from './sentry-http-error';

/**
 * - `report`: Sentry event (error);
 * - `breadcrumb`: only a breadcrumb, visible in the next real event;
 * - `ignore`: nothing, the failure is a normal outcome the caller handles.
 */
export type HttpReportAction = 'report' | 'breadcrumb' | 'ignore';

export interface HttpReportRule {
  readonly method?: string;
  /** Matched against the normalized endpoint path (`/api/v2/lessons/:id`). */
  readonly endpoint: RegExp;
  /** `'any'` covers every failure status, including `0` (network). */
  readonly statuses: readonly number[] | 'any';
  readonly action: Exclude<HttpReportAction, 'report'>;
  readonly reason: string;
}

export const HTTP_REPORT_RULES: readonly HttpReportRule[] = [
  {
    endpoint: /^\/api\/(v1\/event|v2\/event\/track)$/,
    statuses: 'any',
    action: 'ignore',
    reason: 'telemetry',
  },
  {
    method: 'GET',
    endpoint: /^\/api\/v2\/affiliate\/users$/,
    statuses: [404],
    action: 'ignore',
    reason: 'user is not an affiliate',
  },
  {
    method: 'POST',
    endpoint: /\/coupon\/validate$/,
    statuses: [422],
    action: 'ignore',
    reason: 'invalid promo code',
  },
  {
    endpoint: /^\/assets\//,
    statuses: [0],
    action: 'breadcrumb',
    reason: 'asset network failure',
  },
];

export interface HttpReportDecision {
  readonly action: HttpReportAction;
  readonly reason?: string;
}

export interface HttpReportConditions {
  /** Statuses the caller declared as expected (EXPECTED_HTTP_ERROR_STATUSES). */
  readonly expectedStatuses?: readonly number[];
  readonly online: boolean;
  readonly rules?: readonly HttpReportRule[];
}

/** Decides how an HTTP failure (`status === 0` or `>= 400`) is reported. */
export function resolveHttpReportAction(
  failure: Pick<NormalizedHttpFailure, 'method' | 'endpoint' | 'status'>,
  { expectedStatuses = [], online, rules = HTTP_REPORT_RULES }: HttpReportConditions
): HttpReportDecision {
  const { method, endpoint, status } = failure;

  if (expectedStatuses.includes(status)) return { action: 'ignore', reason: 'expected by caller' };

  const rule = rules.find(
    ({ method: ruleMethod, endpoint: pattern, statuses }) =>
      (!ruleMethod || ruleMethod === method.toUpperCase()) &&
      pattern.test(endpoint) &&
      (statuses === 'any' || statuses.includes(status))
  );
  if (rule) return { action: rule.action, reason: rule.reason };

  if (status === 0 && !online) return { action: 'breadcrumb', reason: 'offline' };

  return { action: 'report' };
}
